import { Component, inject, ChangeDetectionStrategy } from '@angular/core';
import { Layout, ThemeMode, ThemeService } from '@core';

@Component({
  selector: 'app-theme-customizer',
  standalone: true,
  imports: [],
  template: `
    <!-- Floating Toggle -->
    <button
      (click)="themeService.toggleCustomizer()"
      class="customizer-toggle shadow-lg"
      [class.active]="themeService.isOpen()"
      [class.rtl]="themeService.direction() === 'rtl'"
    >
      <i class="ri-settings-3-line text-2xl" [class.animate-spin-slow]="!themeService.isOpen()"></i>
    </button>

    <div
      class="customizer-backdrop"
      [class.active]="themeService.isOpen()"
      (click)="themeService.toggleCustomizer()"
    ></div>

    <div
      class="theme-customizer shadow-2xl"
      [class.active]="themeService.isOpen()"
      [class.rtl]="themeService.direction() === 'rtl'"
    >
      <!-- Header -->
      <div
        class="customizer-header p-5 flex items-center justify-between border-b border-gray-100 dark:border-gray-800"
      >
        <div>
          <h3 class="text-sm font-bold text-gray-700 dark:text-gray-400 uppercase tracking-widest">
            Theme Customizer
          </h3>
          <p class="text-[10px] text-gray-400 dark:text-gray-500 font-medium">
            Customize & Preview in Real Time
          </p>
        </div>
        <div class="flex items-center gap-1">
          <button
            (click)="themeService.reset()"
            title="Reset Settings"
            class="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full text-gray-500 transition-colors"
          >
            <i class="ri-refresh-line text-lg"></i>
          </button>
          <button
            (click)="themeService.toggleCustomizer()"
            class="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full text-gray-500 transition-colors"
          >
            <i class="ri-close-line text-xl"></i>
          </button>
        </div>
      </div>

      <!-- Body -->
      <div class="customizer-body overflow-y-auto p-6 space-y-9 h-[calc(100vh-85px)]">
        <!-- THEME MODE -->
        <section>
          <label class="section-label">THEME MODE</label>
          <div class="grid grid-cols-3 gap-4">
            @for (m of modes; track m.value) {
            <button
              (click)="themeService.setMode(m.value)"
              [class.active]="themeService.mode() === m.value"
              class="pref-card"
            >
              <i [class]="m.icon" class="text-xl"></i>
              <span class="text-[11px] font-bold mt-1 uppercase">{{ m.label }}</span>
            </button>
            }
          </div>
        </section>

        <!-- SKIN -->
        <section>
          <label class="section-label">SKIN</label>
          <div class="grid grid-cols-2 gap-4">
            <button
              (click)="themeService.setSkin('default')"
              [class.active]="themeService.skin() === 'default'"
              class="pref-card flex-row gap-3 px-4"
            >
              <i class="ri-checkbox-blank-line text-lg"></i>
              <span class="text-[11px] font-bold uppercase">Default</span>
            </button>
            <button
              (click)="themeService.setSkin('bordered')"
              [class.active]="themeService.skin() === 'bordered'"
              class="pref-card flex-row gap-3 px-4"
            >
              <i class="ri-checkbox-blank-circle-line text-lg"></i>
              <span class="text-[11px] font-bold uppercase">Bordered</span>
            </button>
          </div>
        </section>

        <!-- SEMI DARK MENU (Only in Light Mode) -->
        @if (themeService.mode() === 'light') {
        <section>
          <div class="flex items-center justify-between">
            <label class="section-label m-0">SEMI DARK MENU</label>
            <div
              class="toggle-switch"
              (click)="themeService.setSemiDarkMenu(!themeService.semiDarkMenu())"
            >
              <div class="track" [class.active]="themeService.semiDarkMenu()">
                <div class="thumb"></div>
              </div>
            </div>
          </div>
        </section>
        }

        <!-- PRIMARY COLOR -->
        <section>
          <label class="section-label">PRIMARY COLOR</label>
          <div class="palette-row flex items-center justify-between gap-2">
            @for (c of primaryColors; track c.hex) {
            <button
              (click)="themeService.setPrimaryColor(c.hex)"
              [style.background-color]="c.hex"
              [class.active]="themeService.primaryColor() === c.hex"
              class="color-dot relative group"
            >
              @if (themeService.primaryColor() === c.hex) {
              <i
                class="ri-check-line text-white text-xs"
              ></i>
              }
              <span class="tooltip">{{ c.name }}</span>
            </button>
            }

            <!-- Custom Color Picker -->
            <div class="relative">
              <input
                type="color"
                (input)="onColorChange($event)"
                [value]="isCustomColor() ? themeService.primaryColor() : '#9155fd'"
                class="color-picker-input"
              />
              <button class="color-dot custom-picker" [class.active]="isCustomColor()">
                <i class="ri-palette-line text-sm"></i>
              </button>
            </div>
          </div>
        </section>

        <hr class="border-gray-100 dark:border-gray-800" />

        <div class="pt-4">
          <button
            (click)="themeService.reset()"
            class="w-full py-3 bg-red-50 text-red-600 dark:bg-red-900/20 dark:text-red-400 font-bold rounded-xl active:scale-95 transition-all text-xs tracking-widest uppercase"
          >
            RESET SETTINGS
          </button>
        </div>
      </div>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [
    `
      .customizer-toggle {
        position: fixed;
        top: 50%;
        right: 0;
        transform: translateY(-50%);
        width: 35px;
        height: 35px;
        background: #9155fd;
        color: white;
        border-radius: 12px 0 0 12px;
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 2000;
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        box-shadow: -4px 0 16px rgba(145, 85, 253, 0.5);
      }
      .customizer-toggle.rtl {
        right: auto;
        left: 0;
        border-radius: 0 12px 12px 0;
        box-shadow: 4px 0 16px rgba(145, 85, 253, 0.5);
      }
      .customizer-toggle:hover {
        padding-right: 12px;
        width: 62px;
      }
      .customizer-toggle.rtl:hover {
        padding-right: 0;
        padding-left: 12px;
        width: 62px;
      }
      .customizer-toggle.active {
        display: none;
      }

      @keyframes spin-slow {
        from {
          transform: rotate(0deg);
        }
        to {
          transform: rotate(360deg);
        }
      }
      .animate-spin-slow {
        animation: spin-slow 8s linear infinite;
      }

      .customizer-backdrop {
        position: fixed;
        inset: 0;
        background: rgba(0, 0, 0, 0.4);
        backdrop-filter: blur(4px);
        z-index: 2100;
        opacity: 0;
        pointer-events: none;
        transition: opacity 0.3s ease;
      }
      .customizer-backdrop.active {
        opacity: 1;
        pointer-events: auto;
      }

      .theme-customizer {
        position: fixed;
        top: 0;
        right: 0;
        width: 380px;
        height: 100vh;
        background: var(--card-bg);
        z-index: 2200;
        transform: translateX(100%);
        transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
        border-left: 1px solid var(--border-color);
      }
      .theme-customizer.rtl {
        right: auto;
        left: 0;
        transform: translateX(-100%);
        border-left: none;
        border-right: 1px solid var(--border-color);
      }
      .theme-customizer.active {
        transform: translateX(0);
      }

      .section-label {
        font-size: 11px;
        font-weight: 800;
        color: var(--text-muted);
        letter-spacing: 1.5px;
        margin-bottom: 14px;
        display: block;
      }

      .pref-card {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 14px;
        border-radius: 12px;
        border: 1px solid var(--border-color);
        background: var(--content-bg);
        color: var(--text-secondary);
        transition: all 0.25s;
      }
      .pref-card:hover {
        border-color: var(--primary);
      }
      .pref-card.active {
        background: var(--primary-light);
        border-color: var(--primary);
        color: var(--primary);
      }

      .palette-row {
        display: flex;
        width: 100%;
      }

      .color-dot {
        width: 32px;
        height: 32px;
        border-radius: 50%;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        transition:
          transform 0.2s,
          box-shadow 0.2s;
        flex-shrink: 0;
      }
      .color-dot:hover {
        transform: scale(1.15);
      }
      .color-dot.active {
        box-shadow:
          0 0 0 2px var(--card-bg),
          0 0 0 4px var(--primary);
      }

      .color-picker-input {
        position: absolute;
        inset: 0;
        width: 100%;
        height: 100%;
        opacity: 0;
        cursor: pointer;
        z-index: 5;
      }
      .custom-picker {
        background: var(--border-color);
        color: var(--text-muted);
      }
      .custom-picker.active {
        background: var(--primary);
        color: white;
      }

      .tooltip {
        position: absolute;
        bottom: 100%;
        left: 50%;
        transform: translateX(-50%) translateY(-8px);
        padding: 4px 8px;
        background: #1e293b;
        color: white;
        font-size: 10px;
        border-radius: 4px;
        opacity: 0;
        visibility: hidden;
        transition: all 0.2s;
        white-space: nowrap;
        pointer-events: none;
      }
      .group:hover .tooltip {
        opacity: 1;
        visibility: visible;
        transform: translateX(-50%) translateY(-4px);
      }

      .toggle-switch {
        cursor: pointer;
      }
      .toggle-switch .track {
        width: 38px;
        height: 22px;
        background: #cbd5e1;
        border-radius: 99px;
        padding: 3px;
        transition: background 0.2s;
      }
      .toggle-switch .track.active {
        background: var(--primary);
      }
      .toggle-switch .thumb {
        width: 16px;
        height: 16px;
        background: white;
        border-radius: 50%;
        transition: transform 0.2s;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
      }
      .toggle-switch .track.active .thumb {
        transform: translateX(16px);
      }

      /* Override legacy global button.active::after red underline */
      :host button.active::after,
      .pref-card.active::after,
      .color-dot.active::after,
      .custom-picker.active::after,
      .customizer-toggle.active::after {
        content: none !important;
        display: none !important;
        border: none !important;
      }
    `,
  ],
})
export class ThemeCustomizerComponent {
  themeService = inject(ThemeService);

  modes: { value: ThemeMode; label: string; icon: string }[] = [
    { value: 'light', label: 'Light', icon: 'ri-sun-line' },
    { value: 'dark', label: 'Dark', icon: 'ri-moon-clear-line' },
    { value: 'system', label: 'System', icon: 'ri-computer-line' },
  ];

  primaryColors = [
    { name: 'Red', hex: '#ff4c51' },
    { name: 'Orange', hex: '#ff9f43' },
    { name: 'Yellow', hex: '#ffcd56' },
    { name: 'Green', hex: '#28c76f' },
    { name: 'Blue', hex: '#00cfe8' },
    { name: 'Indigo', hex: '#666cff' },
    { name: 'Violet', hex: '#9155fd' },
  ];

  layouts: { value: Layout; label: string; icon: string }[] = [
    { value: 'vertical', label: 'Vertical', icon: 'ri-layout-left-line' },
    { value: 'collapsed', label: 'Collapsed', icon: 'ri-layout-column-line' },
    { value: 'horizontal', label: 'Horizontal', icon: 'ri-layout-top-line' },
  ];

  isCustomColor() {
    const current = this.themeService.primaryColor();
    return !this.primaryColors.some((c) => c.hex.toLowerCase() === current.toLowerCase());
  }

  onColorChange(event: Event) {
    const color = (event.target as HTMLInputElement).value;
    this.themeService.setPrimaryColor(color);
  }
}
